// DS: C41
import React, { useState } from 'react';
import { BiData, DefaultBiEvent, TagClickBiEvent } from '@wix/da-bi/pkg/events';
import { useChildrenFolding } from '@wix/da-hooks/pkg/useChildrenFolding';
import TagList from '../TagList';
import Tag from '../Tag';
import { Tag as TagType } from './types';

import s from './FoldableTagList.scss';

const MAX_LINES = 2;
const TAG_SPACING = parseInt(s['tag-spacing'], 10);
// TODO: this won't internationalize well
const TOGGLE_BUTTON_WIDTH = parseInt(s['more-button-width'], 10);

const TAG_KEY_FN = tag => tag.name;

export interface Props {
  tags: TagType[];
  className?: string;
  isFoldable?: boolean;
  moreLabel: React.ReactNode;
  lessLabel?: React.ReactNode;
}

/** A TagList that includes a final, tag-like toggle button that
 *  folds or expands the list from 2 lines to as many as needed. */
export const FoldableTagList: React.FC<Props> = ({
  tags,
  className,
  isFoldable = true,
  moreLabel,
  lessLabel,
}) => {
  const [isFolded, setIsFolded] = useState(isFoldable);

  const { sizedItems, hasHidden, containerRef } = useChildrenFolding(tags, {
    maxLines: MAX_LINES,
    itemSpacing: TAG_SPACING,
    endPadding: TOGGLE_BUTTON_WIDTH,
    getItemKey: TAG_KEY_FN,
  });

  const sizedTags = sizedItems.map(({ item, isOverflow }) => ({
    tagname: item.name,
    href: item.url,
    className: isFoldable && isFolded && isOverflow ? s['overflowed'] : undefined,
    biData: BiData<TagClickBiEvent>({
      evid: 391,
      tag_text: item.name,
      link_url: item.url,
    }),
  }));

  return (
    <TagList
      className={className}
      tags={sizedTags as any}
      size="medium"
      isTruncated
      multiline
      ref={containerRef}
    >
      {/* the folding toggle is designed to visually be appended to the tag list,
          so we just pass it as a child in order to control the props better. */}
      {isFoldable &&
        hasHidden &&
        isFolded /* we only support unfolding for now. no folding back */ && (
          <Tag
            /* no collision bc hyphens aren't accepted in tag creation */
            key="more-or-less"
            className={s['more-or-less']}
            size="medium"
            onClick={() => setIsFolded(!isFolded)}
            biData={BiData<DefaultBiEvent>({
              evid: 2,
              click_info: isFolded ? 'more' : 'less',
            })}
          >
            {isFolded ? moreLabel : lessLabel}
          </Tag>
        )}
    </TagList>
  );
};
FoldableTagList.displayName = 'FoldableTagList';

export default FoldableTagList;
